import { inject, Injectable } from "@angular/core";
import { IAuditTrail } from "@ramudden/models/audit-trail";
import { IDateInfoCollection } from "@ramudden/models/date-info";
import { IJournal } from "@ramudden/models/journal";
import { IMeasuringPoint, MeasuringPointCreator, MeasuringPointUpdater } from "@ramudden/models/measuring-point";
import { ISearchResult, SearchParameters, ServiceRequestOptions } from "@ramudden/models/search";
import { IUpload, IUploadDailyMetric, UploadParameters } from "@ramudden/models/upload";
import { DownloadFileService, UploadFileService } from "@ramudden/services";
import { firstValueFrom, Observable } from "rxjs";
import { ApiService } from "./api";

@Injectable({
    providedIn: "root",
})
export class MeasuringPointApi extends ApiService<IMeasuringPoint, MeasuringPointCreator, MeasuringPointUpdater> {
    private readonly downloadFileService = inject(DownloadFileService);
    private readonly uploadFileService = inject(UploadFileService);

    override getRoute(): string {
        return "MeasuringPoints";
    }

    getDefaultUploadParameters$(measuringPointId: number): Observable<UploadParameters> {
        const url = `${super.getUrl()}/${measuringPointId}/DefaultUploadParameters`;
        return this.http.get<UploadParameters>(url);
    }

    getDataDates$(measuringPointIds: number[]): Observable<IDateInfoCollection> {
        const url = `${super.getUrl()}/Dates`;
        return this.postOther$(url, measuringPointIds);
    }

    upload$(measuringPointId: number, uploadParameters: UploadParameters, file: File): Promise<IUpload> {
        const url = `${super.getUrl()}/${measuringPointId}/Upload`;
        const formData = new FormData();
        formData.append("config", JSON.stringify(uploadParameters));
        formData.append("data", file);

        return this.uploadFileService.upload$(url, formData, file);
    }

    getEventOverview$(
        measuringPointId: number,
        from: Date,
        until: Date,
    ): Observable<{ audits: IAuditTrail[]; journals: IJournal[] }> {
        const searchParameters = new SearchParameters();
        searchParameters.queryParams = {
            from: from,
            until: until,
        };

        return this.getOther$<{ audits: IAuditTrail[]; journals: IJournal[] }>(
            `/${measuringPointId}/EventOverview`,
            searchParameters,
        );
    }

    getDailyUploadMetrics$(
        measuringPointId: number,
        skip: number,
        take: number,
    ): Observable<ISearchResult<IUploadDailyMetric>> {
        const urlAppend = `/${measuringPointId}/DailyUploadMetrics`;

        const searchParameters = new SearchParameters();
        searchParameters.skip = skip;
        searchParameters.take = take;

        return this.getOther$<ISearchResult<IUploadDailyMetric>>(urlAppend, searchParameters, null, false);
    }

    public async get(id: number, serviceRequestOptions: ServiceRequestOptions = null): Promise<IMeasuringPoint> {
        return await firstValueFrom(this.get$(id, null, serviceRequestOptions));
    }
}
